import type {PrimaryConfigOptions} from '@/website-builder/grapesjs-website-builder-plugin/types/general';
import type {Point, Position} from '@Utils/math.util';

/**
 * @author Shuja Mahmood <shuja@250mils.com>
 */
interface CustomMouseEvent extends Event {
    button: number;
    which: number;
    buttons: number;
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
    pageX: number;
    pageY: number;
    dataTransfer: DataTransfer;
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
}

interface TouchStateData {
    dragSource: HTMLElement | null;
    touchEvent: TouchEvent | null;
    targetElement: Element | null;
    lastTouchPoint: Point | null;
    dragImage: HTMLElement | null;
    isDragging: boolean;
    dragImageOffset: Position;
    lastClickTimestamp: number;
    eventDataTransfer: DataTransfer;
    iframeOffset: Position;
}

const IFRAME_NODE_NAME = 'IFRAME';
const DRAGGABLE_ATTRIBUTE = 'draggable';

const DRAG_THRESHOLD_PX = 5;
const DRAG_IMAGE_OPACITY = 0.5;
const MAX_DBLCLICK_SEPARATION_MS = 500;
const CONTEXT_MENU_HOLD_DURATION_MS = 900;

const KEYBOARD_PROPS = ['altKey', 'ctrlKey', 'metaKey', 'shiftKey'];
const POINTER_PROPS = ['pageX', 'pageY', 'clientX', 'clientY', 'screenX', 'screenY'];
const REMOVE_ATTRIBUTES = ['id', 'class', 'style', 'draggable'];

export default (opt: PrimaryConfigOptions): void => {
    const {editor} = opt;

    if (!editor) {
        return;
    }

    const createNewTransferData = (): DataTransfer => {
        const dataTransfer = new DataTransfer();
        dataTransfer.dropEffect = 'move';
        dataTransfer.effectAllowed = 'all';

        return dataTransfer;
    };

    const getGrapesJsCanvasIframe = (): HTMLIFrameElement => {
        return editor.Canvas.getFrameEl();
    };

    const shouldHandleTouchEvent = (event: TouchEvent): boolean => {
        return event && !event.defaultPrevented && event.touches && event.touches.length < 2;
    };

    const resetTouchState = (): void => {
        destroyDragImage();
        touchState.dragSource = null;
        touchState.touchEvent = null;
        touchState.targetElement = null;
        touchState.lastTouchPoint = null;
        touchState.isDragging = false;
    };

    const getPoint = (event: TouchEvent, page = false): Point => {
        const touch = event.touches.item(0);

        if (!touch) {
            throw new Error('No touch point found in the touch event');
        }

        return {
            x: page ? touch.pageX : touch.clientX,
            y: page ? touch.pageY : touch.clientY,
        };
    };

    const getDelta = (event: TouchEvent): number => {
        if (!touchState.lastTouchPoint) {
            return 0;
        }

        const point = getPoint(event);

        return Math.abs(point.x - touchState.lastTouchPoint.x) + Math.abs(point.y - touchState.lastTouchPoint.y);
    };

    const getTarget = (event: TouchEvent): Element | null => {
        const point = getPoint(event);
        let element = document.elementFromPoint(point.x, point.y);

        if (element && element.nodeName === IFRAME_NODE_NAME) {
            const iframe = element as HTMLIFrameElement;
            const iframeDocument = iframe.contentWindow?.document;

            if (iframeDocument) {
                const x = point.x - touchState.iframeOffset.left;
                const y = point.y - touchState.iframeOffset.top;

                element = iframeDocument.elementFromPoint(x, y);
            }
        }

        while (element && getComputedStyle(element).pointerEvents === 'none') {
            element = element.parentElement;
        }

        return element;
    };

    const getClosestDraggable = (target: Element | null): HTMLElement | null => {
        let element = target as HTMLElement | null;

        while (element) {
            if (element.getAttribute(DRAGGABLE_ATTRIBUTE)) {
                return element;
            }
            element = element.parentElement;
        }

        return null;
    };

    const copyEventProps = (source: never, destination: never, props: string[]): void => {
        for (const prop of props) {
            destination[prop] = source[prop];
        }
    };

    const fixOffsetPositionForCustomMouseEvent = (event: CustomMouseEvent): void => {
        const x = event.clientX - touchState.iframeOffset.left;
        const y = event.clientY - touchState.iframeOffset.top;

        event.clientX = x;
        event.offsetX = x;
        event.pageX = x;
        event.clientY = y;
        event.offsetY = y;
        event.pageY = y;
    };

    const setDefaultPropertiesOfCustomMouseEvent = (event: CustomMouseEvent): void => {
        event.button = 0;
        event.which = 1;
        event.buttons = 1;
        event.dataTransfer = touchState.eventDataTransfer;
    };

    const copyDragImageStyle = (source: HTMLElement, destination: HTMLElement): void => {
        REMOVE_ATTRIBUTES.forEach(attribute => {
            destination.removeAttribute(attribute);
        });

        if (source instanceof HTMLCanvasElement) {
            const canvasSource = source;
            const canvasDestination = destination as HTMLCanvasElement;

            canvasDestination.width = canvasSource.width;
            canvasDestination.height = canvasSource.height;

            const context = canvasDestination.getContext('2d');
            if (context) {
                context.drawImage(canvasSource, 0, 0);
            }
        }

        const computedStyle = getComputedStyle(source);

        for (const key of Array.from(computedStyle)) {
            if (!key.includes('transition')) {
                // eslint-disable-next-line
                // @ts-ignore
                destination.style.setProperty(key, computedStyle[key] as string);
            }
        }
        destination.style.pointerEvents = 'none';

        for (let i = 0; i < source.children.length; i++) {
            copyDragImageStyle(source.children[i] as HTMLElement, destination.children[i] as HTMLElement);
        }
    };

    const updateIframeOffset = (): void => {
        touchState.iframeOffset.left = 0;
        touchState.iframeOffset.top = 0;

        let parentElement: HTMLElement | null = getGrapesJsCanvasIframe();

        do {
            touchState.iframeOffset.left += parentElement?.offsetLeft ?? 0;
            touchState.iframeOffset.top += parentElement?.offsetTop ?? 0;
            parentElement = parentElement.offsetParent as HTMLElement;
        } while (parentElement);
    };

    const dispatchEvent = (event: TouchEvent | null, type: string, target: Element | null): boolean => {
        if (!event || !target) {
            return false;
        }

        const newEvent = new Event(type, {
            bubbles: true,
            cancelable: true,
        }) as CustomMouseEvent;

        const touch = event.touches.item(0);
        if (!touch) {
            throw new Error('No touch point found in the touch event');
        }

        copyEventProps(event as never, newEvent as never, KEYBOARD_PROPS);
        copyEventProps(touch as never, newEvent as never, POINTER_PROPS);

        setDefaultPropertiesOfCustomMouseEvent(newEvent);
        fixOffsetPositionForCustomMouseEvent(newEvent);

        target.dispatchEvent(newEvent);

        return newEvent.defaultPrevented;
    };

    const createDragImage = (event: TouchEvent): void => {
        if (touchState.dragImage) {
            destroyDragImage();
        }

        if (!touchState.dragSource) {
            return;
        }

        touchState.dragImage = touchState.dragSource.cloneNode(true) as HTMLElement;
        copyDragImageStyle(touchState.dragSource, touchState.dragImage);
        touchState.dragImage.style.top = touchState.dragImage.style.left = '-9999px';

        const clientRect = touchState.dragSource.getBoundingClientRect();
        const point = getPoint(event);
        touchState.dragImageOffset = {
            left: point.x - clientRect.left,
            top: point.y - clientRect.top,
        };
        touchState.dragImage.style.opacity = String(DRAG_IMAGE_OPACITY);

        moveDragImage(event);
        document.body.appendChild(touchState.dragImage);
    };

    const destroyDragImage = (): void => {
        if (touchState.dragImage?.parentElement) {
            touchState.dragImage.parentElement.removeChild(touchState.dragImage);
        }

        touchState.dragImage = null;
    };

    const moveDragImage = (event: TouchEvent): void => {
        requestAnimationFrame(() => {
            if (!touchState.dragImage) {
                return;
            }

            const point = getPoint(event, true);
            const style = touchState.dragImage.style;
            style.position = 'absolute';
            style.pointerEvents = 'none';
            style.zIndex = '999999';
            style.left = `${Math.round(point.x - touchState.dragImageOffset.left)}px`;
            style.top = `${Math.round(point.y - touchState.dragImageOffset.top)}px`;
        });
    };

    const handleTouchStart = (event: TouchEvent): void => {
        if (!shouldHandleTouchEvent(event)) {
            return;
        }

        updateIframeOffset();

        if (Date.now() - touchState.lastClickTimestamp < MAX_DBLCLICK_SEPARATION_MS) {
            if (dispatchEvent(event, 'dblclick', event.target as Element)) {
                event.preventDefault();
                resetTouchState();
                touchState.lastClickTimestamp = 0;

                return;
            }
        }

        resetTouchState();

        const source = getClosestDraggable(event.target as Element);
        if (!source) {
            return;
        }

        if (
            dispatchEvent(event, 'mousemove', event.target as Element) ||
            dispatchEvent(event, 'mousedown', event.target as Element)
        ) {
            return;
        }

        touchState.dragSource = source;
        touchState.lastTouchPoint = getPoint(event);
        touchState.touchEvent = event;

        setTimeout(() => {
            if (touchState.dragSource !== source || touchState.dragImage !== null) {
                return;
            }
            if (dispatchEvent(event, 'contextmenu', source)) {
                resetTouchState();
            }
        }, CONTEXT_MENU_HOLD_DURATION_MS);
    };

    const handleTouchMove = (event: TouchEvent): void => {
        if (!shouldHandleTouchEvent(event)) {
            return;
        }

        const target = getTarget(event);

        if (touchState.dragSource && !touchState.dragImage) {
            const delta = getDelta(event);
            if (delta > DRAG_THRESHOLD_PX) {
                createDragImage(event);
                touchState.isDragging = true;
                dispatchEvent(event, 'dragstart', touchState.dragSource);
                dispatchEvent(event, 'dragenter', target);
            }
        }

        if (touchState.dragImage) {
            touchState.touchEvent = event;

            if (target !== touchState.targetElement) {
                dispatchEvent(touchState.touchEvent, 'dragleave', touchState.targetElement);
                dispatchEvent(event, 'dragenter', target);
                touchState.targetElement = target;
            }

            moveDragImage(event);
            dispatchEvent(event, 'dragover', target);
        }

        if (dispatchEvent(event, 'mousemove', target)) {
            touchState.touchEvent = event;
        }
    };

    const handleTouchEnd = (event: TouchEvent): void => {
        if (!shouldHandleTouchEvent(event)) {
            return;
        }

        destroyDragImage();

        if (touchState.dragSource) {
            if (!touchState.isDragging) {
                dispatchEvent(touchState.touchEvent, 'click', touchState.dragSource);
                touchState.lastClickTimestamp = Date.now();
            }
            if (!event.type.includes('cancel')) {
                dispatchEvent(touchState.touchEvent, 'drop', touchState.targetElement);
            }

            dispatchEvent(touchState.touchEvent, 'dragend', touchState.dragSource);
            resetTouchState();
            event.preventDefault();
            return;
        }

        if (dispatchEvent(touchState.touchEvent, 'mouseup', event.target as Element)) {
            event.preventDefault();
            return;
        }

        const iframe = getGrapesJsCanvasIframe();
        iframe.contentDocument?.dispatchEvent(new Event('mouseup'));
    };

    const touchState: TouchStateData = {
        dragSource: null,
        touchEvent: null,
        targetElement: null,
        lastTouchPoint: null,
        dragImage: null,
        isDragging: false,
        dragImageOffset: {left: 0, top: 0},
        lastClickTimestamp: 0,
        eventDataTransfer: createNewTransferData(),
        iframeOffset: {top: 0, left: 0},
    };

    editor.on('load', () => {
        document.addEventListener('touchstart', handleTouchStart);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
        document.addEventListener('touchcancel', handleTouchEnd);
    });

    editor.on('destroy', () => {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
    });
};
